import { Injectable } from '@angular/core';

import { MessageService } from 'primeng/api';
import { BehaviorSubject, catchError, concatMap, from, map, Observable, Subscription, tap, throwError, toArray } from 'rxjs';
import moment, { Moment } from 'moment-timezone';

import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { JournodexRegionsService } from '../../../../Common/Journodex/services/journodex-regions.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { Mention } from '../../../../Common/Mentions/models/mention';
import { MentionModel } from '../../../../api/mentions/model/mention.model';
import { MentionsApi } from '../../../../api/mentions/mentions.api';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { MediaTypeEnum } from '../../../../Common/Basic/enums/media-type.enum';

import { Author } from '../../models/author';
import { MediaTypeModel } from '../../models/media-type.model';
import { MentionImportFileParameters, MentionImportRequestModel, MentionImportResponseModel } from '../../services/mention-csv-import-dialog.service';
import { Publication } from '../../models/publication';
import { MentionImportArticle } from './newsdesk-mention-import.service';
import { iso3ToIso2 } from './iso3-to-iso2.const';
import { ProgressModel } from '../../../../Common/Dialogs/models/progress.model';

@Injectable()
export class MeltwaterMentionImportService {

    articles: MentionImportArticle[] = [];;

    subscriptions: Subscription[] = [];

    progress: ProgressModel;
    private progress$ = new BehaviorSubject<ProgressModel>(null);

    readonly importBatchSize: number = 200;
    readonly authorNameBlacklist: string[] = ['Uknown', 'Unknown'];

    constructor(
        public mentionsApi: MentionsApi,
        public coreDialogService: CoreDialogService,
        public currentUserStateService: CurrentUserStateService,
        public localizationService: LocalizationService,
        public messageService: MessageService,
        public journodexRegionsService: JournodexRegionsService
    ) {}

    addArticles(file: File): Observable<MentionImportArticle[]> {
        return from(file.arrayBuffer()).pipe(
            map(content => new TextDecoder().decode(new Uint8Array(content))),
            map(htmlContent => {
                const parser = new DOMParser();
                const htmlDoc = parser.parseFromString(htmlContent, 'text/html');
                const articles = htmlDoc.querySelectorAll('table.contentStreamDocument, table.x_contentStreamDocument');
                if (articles.length > 0) {
                    articles.forEach(article => {
                        this.articles.push({ fileName: file.name, mentionRaw: article });
                    });
                }

                return this.articles;
            }),
            catchError(error => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform('Monitoring.mentionImporter.readFileError')
                });
                return throwError(() => new Error(error));
            })
        );
    }

    removeArticles(file: File): void {
        this.articles = this.articles.filter(article => article.fileName !== file.name);
    }

    setMentionsFromRawArticles(): void {
        this.articles.forEach(article => {
            this.setMentionFromRawArticle(article);
        });
    }

    setMentionFromRawArticle(article: MentionImportArticle): void {
        const raw = article.mentionRaw;
        const mention = new Mention();

        const outletLinkText = raw.querySelector('.liteSourceName, .x_liteSourceName')?.textContent.trim();
        if (outletLinkText) {
            let outletName = outletLinkText;
            if (outletLinkText.endsWith('(Print Edition)')) {
                mention.mediaType = new MediaTypeModel(MediaTypeEnum.PRINT);
                outletName = outletName.replace(' (Print Edition)', '');
            } else {
                mention.mediaType = new MediaTypeModel(MediaTypeEnum.ONLINE_NEWS);
            }
            mention.publication = { name: outletName } as Publication;
        }

        const countryCode = raw.querySelector('.listCountryName, .x_listCountryName')?.textContent.trim();
        if (countryCode) {
            const iso2Code = iso3ToIso2[countryCode];
            mention.countryName = this.journodexRegionsService.getCountryByIsoCode(iso2Code)?.name;
        }

        const dateText = raw.querySelector('.liteDatePart, .x_liteDatePart')?.textContent.trim();
        const datePublished: Moment = moment(dateText);
        if (datePublished.year() === 2001) {
            datePublished.year(new Date().getFullYear());
        }
        const timeText = raw.querySelector('.liteTimePart, .x_liteTimePart')?.textContent.trim();
        if (timeText) {
            const time = moment(timeText, 'hh:mm a');
            datePublished.hour(time.hour());
            datePublished.minute(time.minute());
        }
        mention.datePublished = datePublished.utc().toDate();

        const title = raw.querySelector('.contentStreamDocumentTitle, .x_contentStreamDocumentTitle');
        if (title) {
            const titleText = title.textContent.replace(/\n/g, '');
            mention.headline = titleText;
            const titleLink = title.querySelector('.articleClickOpen, .x_articleClickOpen');
            if (titleLink) {
                mention.url = titleLink.getAttribute('href');
            }
        }

        const tagsAndText: string[] = (Array.from(raw.querySelectorAll('.tagsAndText, .x_tagsAndText')) as HTMLElement[]).map(elem => elem.textContent.trim());
        if (tagsAndText.length > 0) {
            const authorPrefix = 'Author: ';
            const author = tagsAndText.find(x => x.startsWith(authorPrefix));
            if (author) {
                const name = author.replace(authorPrefix, '');
                if (!this.authorNameBlacklist.includes(name)) {
                    mention.author = { name: author.replace(authorPrefix, '') } as Author;
                }
            }
            const arPrefix = 'Reach: ';
            const ar = tagsAndText.find(x => x.startsWith(arPrefix));
            if (ar) {
                mention.audienceReach = +ar.replace(arPrefix, '');
            }
            const wcPrefix = 'Length: ';
            const wc = tagsAndText.find(x => x.startsWith(wcPrefix));
            if (wc) {
                mention.wordCount = +wc.replace(wcPrefix, '');
            }
            const sentimentPrefix = 'Tone: ';
            const sentiment = tagsAndText.find(x => x.startsWith(sentimentPrefix));
            if (sentiment) {
                switch (sentiment.replace(sentimentPrefix, '')) {
                    case 'Positive':
                        mention.sentiment = 1;
                        break;
                    case 'Neutral':
                        mention.sentiment = 0;
                        break;
                    case 'Negative':
                        mention.sentiment = -1;
                        break;
                }
            }
        }

        const bodyHTML: HTMLElement = raw.querySelector('.fullText, .x_fullText') || raw.querySelector('.hitSentence, .x_hitSentence');
        if (raw.querySelector('.fullText, .x_fullText')) {
            bodyHTML.innerHTML = bodyHTML.innerHTML
                .replace(/<strong[^>]*>/g, '').replace(/<\/strong>/g, '\n\n')
                .replace(/<p>/g, '&lt;p&gt;').replace(/<\/p>/g, '&lt;/p&gt;'); // retain paragraphs when using textContent below
        }
        mention.body = bodyHTML?.textContent.trim();

        article.mention = mention;
    }

    importData(fileParameters: MentionImportFileParameters & { fileName: string }): Observable<MentionImportResponseModel[]> {
        const mentionModels: MentionModel[] = this.articles.map(article => {
            const mention = article.mention;
            if (fileParameters.folders) {
                mention.folderIds = fileParameters.folders.map(folder => folder.id);
            }
            if (fileParameters.tags) {
                mention.tagIds = fileParameters.tags.map(tag => tag.id);
            }
            return mention.convertToApiMention();
        });

        const apiCalls: Observable<MentionImportResponseModel>[] = [];
        for (let i = 0; i < mentionModels.length; i += this.importBatchSize) {
            const batch = mentionModels.slice(i, i + this.importBatchSize);
            const uniqueFolderIds = new Set<number>(batch.map(b => b?.folderIds ?? []).flat());
            const uniqueTagIds = new Set<number>(batch.map(b => b?.tagIds ?? []).flat());
            const request: MentionImportRequestModel = {
                mentionModels: batch,
                fileName: fileParameters.fileName,
                folderIds: Array.from(uniqueFolderIds),
                tagIds: Array.from(uniqueTagIds),
            };
            apiCalls.push(this.mentionsApi.bulkImport(request));
        }

        this.progress = {
            currentIndex: 0,
            totalItems: apiCalls.length
        };
        this.progress$.next(this.progress);

        return from(apiCalls).pipe(
            concatMap((request) => request.pipe(
                tap(() => {
                    this.progress = {
                        currentIndex: ++this.progress.currentIndex,
                        totalItems: this.progress.totalItems
                    };
                    this.progress$.next(this.progress);
                })
            )),
            toArray()
        );
    }

    clearImportedFiles(): void {
        this.articles = [];
    }

    getArticlesLength(): number {
        return this.articles.length;
    }

    getProgress() {
        return this.progress$;
    }
}
